import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Message, SpinnerService } from 'shared-components';
import { RoyaltiesService } from '../../../services/royalties.service';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';

@Component({
    selector: 'app-mantenimiento-roy-new-edit',
    templateUrl: './new-edit.component.html'
})
export class MantenimientoRoyaltiesNewEditComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  public disabledEntidadControl: Subject<boolean> = new Subject();
  public paises;
  public disabledPaises: Subject<boolean> = new Subject();
  public denominaciones;
  public disabledDenominaciones: Subject<boolean> = new Subject();
  public marcas;
  public disabledMarcas: Subject<boolean> = new Subject();

  public initialStatePaises = 'noSelected';
  public initialStateDenominaciones = 'noSelected';
  public initialStateMarcas = 'noSelected';

  public toCloseSpinner = 0;

  public royalty;
  public _unidadesMedida;

  public newEditForm: UntypedFormGroup;
  public showError: boolean;
  public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
  public mensajes = new Array<Message>();

  @Input() set unidadesMedida(_unidades){
    if(_unidades) this._unidadesMedida = _unidades;
  };

  @Input() royaltyId;

  @Input() entidadesControl;

  @Output() refresh = new EventEmitter<boolean>();
  @Output() mensaje = new EventEmitter<string>();


  constructor(
      private _fb: UntypedFormBuilder,
      private readonly _spinnerService: SpinnerService,
      private readonly _royaltiesService: RoyaltiesService,
      private readonly _dataProvider: DataProvider
  ) {}

  ngOnInit() {
    this.newEditForm = this._fb.group({
      entidadControl: ['', Validators.required],
      pais: [{value: '', disabled: true}, Validators.required],
      denominacion: [{value: '', disabled: true}, Validators.required],
      marca: [{value: '', disabled: true}],
      superficie: [''],
      planta: [''],
      pesoProduccion: [''],
      pesoEtiquetado: ['']
    });

    if(this.royaltyId) {
      this.getRoyalty();
    } else {
      this.newEditForm.get('entidadControl').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((entidadControl) => {
        if (entidadControl) {
          this.getPaisesByEntidadControl(entidadControl.id);
          this.getDenominacionesByEntidadControl(entidadControl.id);
          this.getMarcasByEntidadControl(entidadControl.id);
        } else{
          this.disabledPaises.next(true);
          this.disabledDenominaciones.next(true);
          this.disabledMarcas.next(true);
          this.paises = [];
          this.denominaciones = [];
          this.marcas = [];
          this.newEditForm.get('pais').patchValue('');
          this.newEditForm.get('pais').disable();
          this.newEditForm.get('denominacion').patchValue('');
          this.newEditForm.get('denominacion').disable();
          this.newEditForm.get('marca').patchValue('');
          this.newEditForm.get('marca').disable();
        }
      });

      if(this.entidadesControl.length == 1) {
        this.newEditForm.get('entidadControl').patchValue(this.entidadesControl[0]);
        this.newEditForm.get('entidadControl').disable();
        this.disabledEntidadControl.next(true);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getPaisesByEntidadControl(entidadControlId){
    let filters = {
      entidadControlId: entidadControlId
    };
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.getPaisesByEntidadControl(filters).toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then((resp) => {
      if (resp.lista.length > 0) {
        this.paises = resp.lista;
        if (this.paises.length === 1) {
          this.initialStatePaises = 'allSelected';
          this.newEditForm.get('pais').disable();
          this.disabledPaises.next(true);
        } else {
          this.initialStatePaises = 'noSelected';
          this.newEditForm.get('pais').patchValue('');
          this.newEditForm.get('pais').enable();
          this.disabledPaises.next(false);
        }
      }
    });
  }

  public getDenominacionesByEntidadControl(entidadControlId){
    let filters = {
      entidadControlId: entidadControlId
    };
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.getDenominacionesByPaisEntidadControl(filters).toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then((resp) => {
      if (resp.lista.length > 0) {
        this.denominaciones = resp.lista;
        if (this.denominaciones.length === 1) {
          this.initialStateDenominaciones = 'allSelected';
          this.newEditForm.get('denominacion').disable();
          this.disabledDenominaciones.next(true);
        } else {
          this.initialStateDenominaciones = 'noSelected';
          this.newEditForm.get('denominacion').patchValue('');
          this.newEditForm.get('denominacion').enable();
          this.disabledDenominaciones.next(false);
        }
      }
    });
  }

  public getMarcasByEntidadControl(entidadControlId){
    let filters = {
      entidadControlId: entidadControlId
    };
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.getMarcasByPaisEntidadControlDeno(filters).toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then((resp) => {
      if (resp.lista.length > 0) {
        this.marcas = resp.lista;
        this.newEditForm.get('marca').enable();
        this.disabledMarcas.next(false);
      }
    });
  }

  public getRoyalty(){
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._royaltiesService.getRoyalty(this.royaltyId).toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then((resp) => {
      this.royalty = resp;
      this.denominaciones = [{ id: resp.denominacionId, des: resp.desDenominacion }];
      this.marcas = [{ id: resp.marcaId, des: resp.desMarca }];
      this.newEditForm.patchValue({
        entidadControl: this.entidadesControl.filter(a=>a.id == resp.entidadControlId)[0],
        pais: this._dataProvider.paises.filter(p=>p.id === resp.paisId)[0],
        denominacion: this.denominaciones[0],
        marca: this.marcas[0],
        superficie: resp.superficie_Fee,
        planta: resp.nPlantas_Fee,
        pesoProduccion: resp.kilos_Productor_Fee,
        pesoEtiquetado: resp.kilos_Etiquetador_Fee
      });
      this.newEditForm.get('entidadControl').disable();
      this.newEditForm.get('pais').disable();
      this.newEditForm.get('denominacion').disable();
      this.newEditForm.get('marca').disable();
      this.disabledEntidadControl.next(true);
      this.disabledPaises.next(true);
      this.disabledDenominaciones.next(true);
      this.disabledMarcas.next(true);
    }, (error) => {
      this.showApiError = true;
      if (error.status === 420) {
        this.mensajes.push({ class: 'error', message: 'global.error.noAccess' });
      } else {
        this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
      }
    });
  }

  public close() {
      this.refresh.emit(false);
  }

  public save() {
    this.checkForm();
    if (this.newEditForm.valid && !this.royaltyId) {
      const royalty = {
        entidadControlId: this.newEditForm.get('entidadControl').value.id,
        lsPaisId: this.newEditForm.get('pais').value,
        lsDenominacionId: this.newEditForm.get('denominacion').value,
        lsMarcaId: this.newEditForm.get('marca').value,
        superficie_Fee: this.newEditForm.get('superficie').value ? this.newEditForm.get('superficie').value : 0,
        nPlantas_Fee: this.newEditForm.get('planta').value ? this.newEditForm.get('planta').value : 0,
        kilos_Productor_Fee: this.newEditForm.get('pesoProduccion').value ? Number(this.newEditForm.get('pesoProduccion').value) : 0,
        kilos_Etiquetador_Fee: this.newEditForm.get('pesoEtiquetado').value ? Number(this.newEditForm.get('pesoEtiquetado').value) : 0
      };
      this.mensajes = [];
      this.showError = false;
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._royaltiesService.createRoyaltyMultiple(royalty).toPromise().finally(() => {
        this.checkCloseSpinner();
      }).then((resp) => {
        if (resp) {
          this.refresh.emit(true);
          this.mensaje.emit('global.success.itemCreated');
        } else {
          this.showError = true;
          this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        }
      }, (error) => {
        this.showError = true;
        switch (error.error.ApiErrorCode) {
          case 6167: //RoyaltyNoAccesible 
            this.mensajes.push({ class: 'error', message: 'mantenimientoRoyalties.error.royaltyNoAccesible' });
            break;
          case 6168: // RoyaltyYaExiste 
            this.mensajes.push({ class: 'error', message: 'mantenimientoRoyalties.error.royaltyYaExiste' });
            break;
          default:
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            break;
        }
      });
    } else if (this.newEditForm.valid && this.royaltyId) {
      const royalty = {
        id: this.royaltyId,
        entidadControlId: this.newEditForm.get('entidadControl').value.id,
        paisId: this.newEditForm.get('pais').value.id,
        denominacionId: this.newEditForm.get('denominacion').value.id,
        marcaId: this.newEditForm.get('marca').value.id,
        superficie_Fee: this.newEditForm.get('superficie').value ? this.newEditForm.get('superficie').value : 0,
        nPlantas_Fee: this.newEditForm.get('planta').value ? this.newEditForm.get('planta').value : 0,
        kilos_Productor_Fee: this.newEditForm.get('pesoProduccion').value ? Number(this.newEditForm.get('pesoProduccion').value) : 0,
        kilos_Etiquetador_Fee: this.newEditForm.get('pesoEtiquetado').value ? Number(this.newEditForm.get('pesoEtiquetado').value) : 0
      };
      this.mensajes = [];
      this.showError = false;
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._royaltiesService.updateRoyalty(royalty).toPromise().finally(() => {
        this.checkCloseSpinner();
      }).then((resp) => {
        this.refresh.emit(true);
        this.mensaje.emit('global.success.itemUpdated');
      }, (error) => {
        this.showError = true;
        switch (error.error.ApiErrorCode) {
          case 6167: //RoyaltyNoAccesible 
            this.mensajes.push({ class: 'error', message: 'mantenimientoRoyalties.error.royaltyNoAccesible' });
            break;
          case 6168: // RoyaltyYaExiste 
            this.mensajes.push({ class: 'error', message: 'mantenimientoRoyalties.error.royaltyYaExiste' });
            break;
          default:
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            break;
        }
      });
    }
  }

  private checkForm() {
    this.showError = this.newEditForm.invalid;
    if (this.showError) {
      this.mensajes.push({ class: 'error', message: 'global.error.required' });
    }
  }

  private checkCloseSpinner() {
    this.toCloseSpinner -= 1;
    if (this.toCloseSpinner === 0) {
        this._spinnerService.hideSpinner();
    }
  }

  shareCheckedListPaises(item: any[]) {
    this.newEditForm.get('pais').patchValue(item);
  }

  shareCheckedListDenominaciones(item: any[]) {
    this.newEditForm.get('denominacion').patchValue(item);
  }

  shareCheckedListMarcas(item: any[]) {
    this.newEditForm.get('marca').patchValue(item);
  }


}
